import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map, take } from 'rxjs/operators';
import { AuthReturnUrlService } from '../auth/auth-return-url.service';
import { TenantStorageService } from '@core/services/tenant-storage.service';
import { SessionExpirationService } from '../auth/session-expiration.service';


const isInAuthCallback = (u: string) =>
  u.includes('code=') || u.includes('error=') || u.includes('/auth/callback');

/**
 * Authentication guard with complete tenant management
 *
 * Responsibilities:
 * 1. Detect tenant changes and handle re-authentication
 * 2. Check authentication and launch authorization if necessary
 * 3. Centralize all authentication logic
 */
export const authWithRedirectGuard: CanActivateFn = (route, state) => {
  const oidcSecurityService = inject(OidcSecurityService);
  const authReturnUrlService = inject(AuthReturnUrlService);
  const router = inject(Router);
  const tenantStorageService = inject(TenantStorageService);
  const sessionExpirationService = inject(SessionExpirationService);

  // Exclude authentication routes
  const excludedPaths = ['/auth'];
  if (excludedPaths.some(path => state.url.startsWith(path))) {
    return true;
  }

  const urlTenant = route.queryParamMap.get('inst');
  const storedTenant = tenantStorageService.getTenant();

  return oidcSecurityService.isAuthenticated$.pipe(
    take(1),
    map(({ isAuthenticated }) => {
      if (urlTenant && storedTenant && urlTenant !== storedTenant) {

        // Avoid redundant calls
        const currentUrl = state.url;
        if (isInAuthCallback(currentUrl)) {
          return false;
        }

        // 1. Update tenant in storage
        tenantStorageService.setTenant(urlTenant);

        // 2. Save URL for post-authentication redirection
        authReturnUrlService.saveCurrentUrl(state.url);

        // 3. Disconnect current user (local only)
        // GEN-7589 : changement de base voulu par l'utilisateur — `logoffLocal()` bascule
        // `isAuthenticated$`, il ne doit pas etre pris pour une expiration de session.
        sessionExpirationService.markIntentionalLogoff();
        oidcSecurityService.logoffLocal();

        // 4. Restart authentication with the new tenant
        oidcSecurityService.authorize(null, { customParams: { acr_values: `tenant:${urlTenant}` } });

        return false;
      }

      if (isAuthenticated) {
        return true;
      }

      // Check that a tenant is available
      const currentTenant = urlTenant || storedTenant;
      if (!currentTenant) {
        router.navigate(['/auth/no-tenant']);
        return false;
      }

      // Avoid redundant calls
      const currentUrl = window.location.href;
      if (currentUrl.includes('code=') || currentUrl.includes('error=') || currentUrl.includes('/auth/callback')) {
        return false;
      }

      // Save URL and launch authorization
      authReturnUrlService.saveCurrentUrl(state.url);
      oidcSecurityService.authorize(null, { customParams: { acr_values: `tenant:${currentTenant}` } });

      return false;
    })
  );
};


